import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { MessageCircle, X, Send, ChevronDown } from 'lucide-react';
import { CLINIC_NAME, CLINIC_PHONE, CLINIC_ADDRESS } from '../utils/clinicConfig';

function buildMessage(type, data) {
  const { name, slot, date, tokenNumber } = data;
  const clinicLine = `${CLINIC_NAME}${CLINIC_PHONE ? ' | ' + CLINIC_PHONE : ''}`;

  // Note: using simple text symbols instead of 4-byte emoji (e.g. calendar emoji)
  // because some older Android/WhatsApp versions show them as boxes.
  // WhatsApp bold: wrap in *asterisks*
  const templates = {
    appointment_reminder:
`Hello ${name},

This is a reminder for your appointment at *${CLINIC_NAME}*.

Date  : *${date}*
Time  : *${slot || 'As scheduled'}*
Token : *#${tokenNumber}*

Please arrive 5 minutes early.
To reschedule, please call us.

${CLINIC_ADDRESS ? CLINIC_ADDRESS + '\n' : ''}${CLINIC_PHONE ? 'Ph: ' + CLINIC_PHONE : ''}

To check your queue status, visit:
${window.location.origin}/portal
Enter your registered mobile number to see your token status.

Thank you! - ${CLINIC_NAME}`,

    appointment_confirmed:
`Hello ${name},

Your appointment at *${CLINIC_NAME}* has been *confirmed* ✓

Date  : *${date}*
Time  : *${slot || 'As scheduled'}*
Token : *#${tokenNumber}*

We look forward to seeing you.
${CLINIC_PHONE ? 'Ph: ' + CLINIC_PHONE : clinicLine}

To check your queue status on the day:
${window.location.origin}/portal
Enter your registered mobile number.`,

    followup:
`Hello ${name},

Greetings from *${CLINIC_NAME}*!

We hope you are recovering well after your recent visit.

If you have any questions or concerns, please feel free to contact us.

${CLINIC_PHONE ? 'Ph: ' + CLINIC_PHONE : clinicLine}

Take care!`,

    reschedule:
`Hello ${name},

We are following up regarding your appointment at *${CLINIC_NAME}*.

Could you please confirm if your appointment on *${date}* at *${slot || 'the scheduled time'}* is still convenient?

Please reply to confirm or reschedule.
${CLINIC_PHONE ? 'Ph: ' + CLINIC_PHONE : clinicLine}`,

    payment_reminder:
`Hello ${name},

This is a gentle reminder from *${CLINIC_NAME}* regarding an outstanding balance on your account.

Kindly visit us at your earliest convenience to clear the dues.

${CLINIC_PHONE ? 'Ph: ' + CLINIC_PHONE : clinicLine}

Thank you for your cooperation.
- ${CLINIC_NAME}`,
  };

  return templates[type] || templates.appointment_reminder;
}

function formatPhone(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return '91' + digits;
  if (digits.startsWith('91') && digits.length === 12) return digits;
  if (digits.startsWith('0') && digits.length === 11) return '91' + digits.slice(1);
  return digits;
}

const BILL_SUMMARY_KEY = 'bill_summary';

const TEMPLATES = [
  { value: 'appointment_reminder',  label: '(*)  Appointment Reminder' },
  { value: 'appointment_confirmed', label: '(ok) Appointment Confirmed' },
  { value: 'followup',              label: '(>)  Follow-up Message' },
  { value: 'reschedule',            label: '(?)  Reschedule Request' },
  { value: 'payment_reminder',      label: '($)  Payment Reminder' },
];

export default function WhatsAppButton({ patient, slot, date, tokenNumber, size = 'sm', billMode = false }) {
  const [open, setOpen]               = useState(false);
  const [selectedType, setSelectedType] = useState(billMode ? BILL_SUMMARY_KEY : 'appointment_reminder');
  const [customMsg, setCustomMsg]     = useState('');
  const [useCustom, setUseCustom]     = useState(false);
  const [dropPos, setDropPos]         = useState({ top: 0, left: 0, width: 320 });
  const btnRef = useRef(null);

  const phone = patient?.phone || '';
  const name  = patient?.name  || 'Patient';
  const data  = { name, slot, date, tokenNumber };
  const billMsg  = patient?._billMessage || '';
  const message  = useCustom ? customMsg
                 : selectedType === BILL_SUMMARY_KEY ? billMsg
                 : buildMessage(selectedType, data);
  const waUrl   = `https://wa.me/${formatPhone(phone)}?text=${encodeURIComponent(message)}`;

  const PANEL_W = 340;

  function calcPos() {
    if (!btnRef.current) return null;
    const rect = btnRef.current.getBoundingClientRect();
    const panelH = panelRef.current ? panelRef.current.offsetHeight : 520;
    let left = rect.right - PANEL_W;
    if (left < 8) left = rect.left;
    if (left + PANEL_W > window.innerWidth - 8) left = window.innerWidth - PANEL_W - 8;
    let top = rect.bottom + 6;
    if (top + panelH > window.innerHeight - 8) top = Math.max(8, rect.top - panelH - 6);
    return { top, left, width: PANEL_W };
  }

  // Re-measure after panel mounts so the actual height is used
  useEffect(() => {
    if (!open || !panelRef.current) return;
    const pos = calcPos();
    if (pos) setDropPos(pos);
  }, [open]); // eslint-disable-line

  function openDropdown() {
    if (!btnRef.current) return;
    // Initial rough position (before panel renders)
    const rect = btnRef.current.getBoundingClientRect();
    let left = rect.right - PANEL_W;
    if (left < 8) left = rect.left;
    if (left + PANEL_W > window.innerWidth - 8) left = window.innerWidth - PANEL_W - 8;
    setDropPos({ top: rect.bottom + 6, left, width: PANEL_W });
    setOpen(true);
  }

  // Close on resize only — NOT on scroll (user may scroll inside the textarea)
  // We use a ref on the panel so we can detect if the scroll is inside it
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    function handleScroll(e) {
      // If the scroll happened inside the dropdown panel, don't close
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    }

    function handleResize() { setOpen(false); }

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [open]);

  if (!phone) {
    return (
      <span title="No phone number on file"
        style={{ cursor: 'not-allowed', opacity: 0.4, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#25d366' }}>
        <MessageCircle size={13} /> No phone
      </span>
    );
  }

  const dropdown = open && ReactDOM.createPortal(
    <>
      {/* Full-screen backdrop — catches outside clicks */}
      <div
        onClick={() => setOpen(false)}
        style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
      />
      {/* Dropdown panel — fixed position so it escapes table/card overflow */}
      <div
        ref={panelRef}
        onClick={e => e.stopPropagation()}
        style={{
          position:  'fixed',
          top:       dropPos.top,
          left:      dropPos.left,
          width:     dropPos.width,
          zIndex:    9999,
          background: 'var(--color-surface)',
          border:    '2px solid var(--color-border)',
          borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
          overflow: 'hidden',
          maxHeight: '92vh',
          display: 'flex', flexDirection: 'column',
        }}>

        {/* ── Header ── */}
        <div style={{ background: '#075e54', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageCircle size={15} color="#fff" />
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>WhatsApp</div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, lineHeight: 1.2 }}>{name}{phone ? ` · ${phone}` : ''}</div>
            </div>
          </div>
          <button onClick={() => setOpen(false)}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', padding: 5, display: 'flex', alignItems: 'center', borderRadius: 6 }}>
            <X size={15} />
          </button>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
          {/* Template selector */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-ink-soft)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Message Type
            </label>
            <select
              className="select"
              style={{ width: '100%', fontSize: 12 }}
              value={useCustom ? '__custom__' : selectedType}
              onChange={e => {
                if (e.target.value === '__custom__') {
                  setUseCustom(true);
                  if (!customMsg) setCustomMsg(selectedType === BILL_SUMMARY_KEY ? billMsg : buildMessage(selectedType, data));
                } else {
                  setUseCustom(false);
                  setSelectedType(e.target.value);
                }
              }}>
              {billMode && <option value={BILL_SUMMARY_KEY}>📋 Bill Summary (default)</option>}
              {TEMPLATES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              <option value="__custom__">✏️ Custom Message</option>
            </select>
          </div>

          {/* Message preview / editor */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-ink-soft)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {useCustom ? 'Your message' : 'Preview — click to edit'}
            </label>
            <textarea
              value={message}
              onChange={e => { setUseCustom(true); setCustomMsg(e.target.value); }}
              rows={8}
              style={{
                width: '100%', fontSize: 13, lineHeight: 1.65,
                padding: '9px 11px', borderRadius: 8,
                border: `1px solid ${useCustom ? 'var(--color-primary)' : 'var(--color-border)'}`,
                background: 'var(--color-surface-raised)',
                color: 'var(--color-ink)',
                resize: 'vertical',
                fontFamily: '"Segoe UI Emoji","Apple Color Emoji","Noto Color Emoji","Android Emoji",system-ui,sans-serif',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Send button */}
          <a href={waUrl} target="_blank" rel="noopener noreferrer"
            onClick={() => setTimeout(() => setOpen(false), 400)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '11px 0', borderRadius: 8,
              background: '#25d366', color: '#fff',
              fontWeight: 700, fontSize: 14, textDecoration: 'none',
              boxSizing: 'border-box',
            }}>
            <Send size={15} /> Open WhatsApp &amp; Send
          </a>
          <div style={{ fontSize: 11, color: 'var(--color-ink-faint)', textAlign: 'center' }}>
            Opens WhatsApp with the message ready — tap Send there.
          </div>
        </div>
            </div>
    </>,
    document.body
  );

  return (
    <>
      <button
        ref={btnRef}
        onClick={openDropdown}
        title={`Send WhatsApp to ${name}`}
        className="btn btn-sm"
        style={{
          background: '#25d366', color: 'white', border: 'none',
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: size === 'xs' ? 11 : 13,
          padding: size === 'xs' ? '4px 8px' : '6px 12px',
          flexShrink: 0,
        }}>
        <MessageCircle size={size === 'xs' ? 12 : 14} />
        {size !== 'xs' && 'WhatsApp'}
        <ChevronDown size={10} style={{ opacity: 0.8 }} />
      </button>
      {dropdown}
    </>
  );
}
